import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    Divider,
    Button,
    Card,
    CardContent,
    IconButton,
    useTheme,
    useMediaQuery,
    CircularProgress,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    Receipt as ReceiptIcon,
    CalendarMonth as CalendarIcon,
    AccountBalance as BalanceIcon,
    Paid as PaidIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { callAPI, Method } from '../../commonFunctions/CommonFunctions';

// Define types based on your data structure
interface ActualPayment {
    _id: string;
    receiptNumber: string;
    amount: number;
    principal: number;
    interest: number;
    repaymentDate: string;
    remarks: string;
}

interface RepaymentDetail {
    month: number;
    dueDate: string;
    scheduledEMI: number;
    scheduledPrincipal: number;
    scheduledInterest: number;
    status: string;
    actualPayment: ActualPayment;
    loan: any;
    isAdditionalPayment: boolean;
}

const RepaymentDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { repaymentId } = useParams<{ repaymentId: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(true);
    const [repaymentData, setRepaymentData] = useState<RepaymentDetail>();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await callAPI(`repayments/getRepaymentBy/${repaymentId}`, Method.GET);
                console.log('Fetched Repayment Data:', res);
                setRepaymentData(res.repayments);
            } catch (error: any) {
                console.error("Failed to fetch data:", error);
                if (error.message === 'GlobalHandledError') return;
            } finally { setLoading(false) }
        };

        fetchData();
    }, [repaymentId]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'paid':
                return 'success';
            case 'pending':
                return 'warning';
            case 'overdue':
                return 'error';
            default:
                return 'default';
        }
    };

    const handleExportPDF = () => {
        if (!repaymentData || !repaymentData.actualPayment) {
            console.error("Repayment data or actualPayment is not available.");
            return; // Exit early if repaymentData is undefined
        }

        const doc = new jsPDF();

        // Add title
        doc.setFontSize(18);
        doc.text('Repayment Details', 14, 22);

        // Add repayment information
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text(`Receipt Number: ${repaymentData.actualPayment.receiptNumber}`, 14, 32);
        doc.text(`Month: ${repaymentData.month}`, 14, 39);
        doc.text(`Status: ${repaymentData.status}`, 14, 46);

        // Create table data
        const tableData = [
            ['Due Date', formatDate(repaymentData.dueDate)],
            ['Repayment Date', formatDate(repaymentData.actualPayment.repaymentDate)],
            ['Scheduled EMI', formatCurrency(repaymentData.scheduledEMI)],
            ['Scheduled Principal', formatCurrency(repaymentData.scheduledPrincipal)],
            ['Scheduled Interest', formatCurrency(repaymentData.scheduledInterest)],
            ['Amount Paid', formatCurrency(repaymentData.actualPayment.amount)],
            ['Principal Paid', formatCurrency(repaymentData.actualPayment.principal)],
            ['Interest Paid', formatCurrency(repaymentData.actualPayment.interest)],
        ];

        // Add table
        autoTable(doc, {
            startY: 55,
            head: [['Description', 'Value']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: [63, 81, 181],
                textColor: 255,
                fontStyle: 'bold'
            },
            styles: {
                fontSize: 10,
                cellPadding: 3,
            },
            margin: { top: 55 },
        });

        // Add footer
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text('Generated on: ' + new Date().toLocaleDateString(), 14, pageHeight - 10);

        // Save the PDF
        doc.save(`repayment-receipt-${repaymentData.actualPayment.receiptNumber}.pdf`);
    };

    if (loading)
        return (
            <Box height="80vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress sx={{ color: "#4AD06E" }} />
            </Box>
        );

    if (!repaymentData) return <Typography>Repayment Not Found</Typography>;

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ mr: 2 }}
                    size={isMobile ? "small" : "medium"}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" fontWeight="700">
                    Repayment Details
                </Typography>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="contained"
                    color='success'
                    startIcon={<PdfIcon />}
                    onClick={handleExportPDF}
                    sx={{ ml: 2 }}
                    size={isMobile ? "small" : "medium"}
                >
                    Export PDF
                </Button>
            </Box>

            {/* ✅ ADD: Loan Information Banner */}
            {repaymentData.loan && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'primary.light', color: 'white' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight="500">
                                Loan ID
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                                {repaymentData.loan.loanId}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight="500">
                                Applicant
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                                {repaymentData.loan.applicantName}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight="500">
                                Loan Amount
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                                {formatCurrency(repaymentData.loan.loanAmount)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" fontWeight="500">
                                Interest Rate
                            </Typography>
                            <Typography variant="body1" fontWeight="600">
                                {repaymentData.loan.interestRate}%
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Grid container spacing={3}>
                {/* Main Details Card */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Receipt #{repaymentData.actualPayment.receiptNumber}
                                </Typography>
                                <Typography variant="h4" fontWeight="700" gutterBottom>
                                    {formatCurrency(repaymentData.actualPayment.amount)}
                                </Typography>

                                {/* ✅ ADD: Additional Payment Indicator */}
                                {repaymentData.isAdditionalPayment && (
                                    <Chip
                                        label="Additional Payment"
                                        color="warning"
                                        variant="filled"
                                        size="small"
                                        sx={{ fontWeight: 600, fontSize: '0.8rem', mt: 1 }}
                                    />
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                <Chip
                                    label={repaymentData.status}
                                    color={getStatusColor(repaymentData.status) as any}
                                    variant="filled"
                                    size="medium"
                                    sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                                />

                                {/* ✅ ADD: Payment Type Indicator */}
                                {repaymentData.isAdditionalPayment ? (
                                    <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                        Beyond original schedule
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="success.main" sx={{ fontStyle: 'italic' }}>
                                        Scheduled payment
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <CalendarIcon color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="h6">Dates</Typography>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {repaymentData.isAdditionalPayment ? 'Payment Date' : 'Due Date'}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {formatDate(repaymentData.dueDate)}
                                            </Typography>
                                            {!repaymentData.isAdditionalPayment && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Scheduled due date
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Actual Payment Date
                                            </Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {formatDate(repaymentData.actualPayment.repaymentDate)}
                                            </Typography>

                                            {/* ✅ ADD: Late Payment Indicator */}
                                            {!repaymentData.isAdditionalPayment &&
                                                new Date(repaymentData.actualPayment.repaymentDate) > new Date(repaymentData.dueDate) && (
                                                    <Typography variant="caption" color="warning.main">
                                                        Paid after due date
                                                    </Typography>
                                                )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <BalanceIcon color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="h6">
                                                {repaymentData.isAdditionalPayment ? 'Payment Info' : 'Installment'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {repaymentData.isAdditionalPayment ? 'Payment Type' : 'Month'}
                                            </Typography>
                                            <Typography variant="body1" fontWeight="500">
                                                {repaymentData.isAdditionalPayment ? 'Additional Payment' : `Month ${repaymentData.month}`}
                                            </Typography>
                                        </Box>

                                        {/* ✅ UPDATE: Show scheduled EMI only for non-additional payments */}
                                        {!repaymentData.isAdditionalPayment && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Scheduled EMI
                                                </Typography>
                                                <Typography variant="body1" fontWeight="500">
                                                    {formatCurrency(repaymentData.scheduledEMI)}
                                                </Typography>

                                                {/* ✅ ADD: Payment Difference Indicator */}
                                                {repaymentData.actualPayment.amount !== repaymentData.scheduledEMI && (
                                                    <Typography
                                                        variant="caption"
                                                        color={repaymentData.actualPayment.amount > repaymentData.scheduledEMI ? "success.main" : "warning.main"}
                                                    >
                                                        {repaymentData.actualPayment.amount > repaymentData.scheduledEMI ?
                                                            `+${formatCurrency(repaymentData.actualPayment.amount - repaymentData.scheduledEMI)} extra` :
                                                            `${formatCurrency(repaymentData.scheduledEMI - repaymentData.actualPayment.amount)} less`
                                                        }
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Payment Breakdown Card */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <PaidIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Payment Breakdown</Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Principal Amount
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {formatCurrency(repaymentData.actualPayment.principal)}
                            </Typography>

                            {/* ✅ ADD: Scheduled vs Actual Comparison */}
                            {!repaymentData.isAdditionalPayment && repaymentData.scheduledPrincipal > 0 && (
                                <Typography
                                    variant="caption"
                                    color={repaymentData.actualPayment.principal === repaymentData.scheduledPrincipal ? "text.secondary" : "warning.main"}
                                >
                                    {repaymentData.actualPayment.principal === repaymentData.scheduledPrincipal ?
                                        'As scheduled' :
                                        `Scheduled: ${formatCurrency(repaymentData.scheduledPrincipal)}`
                                    }
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Interest Amount
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {formatCurrency(repaymentData.actualPayment.interest)}
                            </Typography>

                            {/* ✅ ADD: Scheduled vs Actual Comparison */}
                            {!repaymentData.isAdditionalPayment && repaymentData.scheduledInterest > 0 && (
                                <Typography
                                    variant="caption"
                                    color={repaymentData.actualPayment.interest === repaymentData.scheduledInterest ? "text.secondary" : "warning.main"}
                                >
                                    {repaymentData.actualPayment.interest === repaymentData.scheduledInterest ?
                                        'As scheduled' :
                                        `Scheduled: ${formatCurrency(repaymentData.scheduledInterest)}`
                                    }
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total Paid
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                {formatCurrency(repaymentData.actualPayment.amount)}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="h6" color="primary.main">
                                Remarks:
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {repaymentData.actualPayment.remarks};
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Remarks Card */}
                {repaymentData.actualPayment.remarks && (
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <InfoIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Remarks</Typography>
                            </Box>

                            <Typography variant="body1">
                                {repaymentData.actualPayment.remarks}
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default RepaymentDetailPage;